<template>
  <b-row class="mt-4">
    <b-col cols="12" md="6">
      <b-card
        title="Iniciador"
        class="border shadow-sm bg-light"
      >
        <b-card-text>Número: {{ data.processNumber }}</b-card-text>
        <b-card-text>
          Iniciador: {{ data.processStarter.name }}
          <template v-if="starterType === 'people' && currentStarter">
           - {{ currentStarter.personIdType.name }}: {{ currentStarter.personId }}
          </template>
        </b-card-text>
        <div class="d-flex" style="gap: 15px">
          <LmButton
            value="Ver detalles"
            @click="onStarterDetails"
          />
          <LmButton
            @click="onViewCoverPage"
            value="Ver carátula"
          />
          <LmButton
            value="Ver más"
            @click="$emit('onEditProcess')"
          />
        </div>
      </b-card>
    </b-col>
    <b-col cols="12" md="6">
      <b-card
        title="Trámites asociados"
        class="border shadow-sm bg-light"
      >
        <b-button
          variant="link"
          @click="$bvModal.show('linked-processes-modal')"
          class="p-0 show-details-button"
          v-if="data.processRelationships.length"
        >
          Ver {{ data.processRelationships.length }} trámites asociados
        </b-button>
        <span v-else class="text-muted font-italic">No hay trámites asociados.</span>
        <LmButton
          v-if="actions.processCanBeEditedByMe && data.lumbreStatus.id === '3'"
          class="mt-4"
          value="Asociar trámite"
          @click="$emit('onLinkProcess')"
        />
      </b-card>
    </b-col>
    <b-col cols="12" md="6">
      <b-card
        title="Ubicación actual"
        class="border shadow-sm bg-light"
      >
        <b-card-text class="mb-1">Trámite en posesión de {{ currentMovementInfo.name }}.</b-card-text>
        <b-card-text class="my-0" v-if="currentMovementInfo.area">Área: {{ currentMovementInfo.area }}</b-card-text>
        <b-card-text v-if="currentMovementInfo.subarea">Subárea: {{ currentMovementInfo.subarea }}</b-card-text>
        <div class="d-flex" style="gap: 15px">
          <LmButton
            value="Realizar pase"
            @click="$emit('onNewPass')"
            v-if="actions.processCanAllowMeToAddMovements"
          />
        </div>
      </b-card>
    </b-col>
    <b-col cols="12" md="6">
      <b-card
        title="Último estado"
        class="border shadow-sm bg-light"
      >
        <b-card-text>Estado actual: {{ data.lumbreStatus.name }}</b-card-text>
        <div class="d-flex" style="gap: 15px">
          <LmButton
            value="Cambiar estado"
            @click="$emit('onChangeStatus')"
            v-if="actions.processCanBeChangedStatusByMe"
          />
        </div>
      </b-card>
    </b-col>
    <b-modal id="coverPagePdfModal" size="xl" hide-footer :title="coverPageInPdf.name">
      <iframe v-if="coverPageInPdf.src" id="pdfViewer" :src="coverPageInPdf.src" width="100%" :height="700"/>
    </b-modal>

    <b-modal
      id="person-new-or-edit-modal"
      title="Detalles"
      hide-footer
      centered
      size="xl"
      v-if="currentStarter"
    >
      <LmPeopleNewOrEdit
        :peopleTypeId="currentStarter.peopleTypeId"
        :personData="currentStarter"
        @onUpdatePerson="onUpdatePerson"
        @cancel="onPeopleNewOrEditCancel"
      />
    </b-modal>

    <b-modal
      id="initiator-edit-modal"
      :title="`Buscar`"
      hide-footer
      centered
      size="xl"
      v-if="starterType === 'initiator'"
    >
      <h3 class="mb-0">Iniciador: {{ data.processStarter.name }}</h3>
      <LmFormBuilder
        class="px-0 mt-3"
        v-if="initiatorData"
        ref="lmFormbuilder-initiator"
        :endpointUriwhereGetComponents="initiatorData.formbuilderPath"
        :formValues="data.initiatorFields"
        @formObjectToSendToServerChange="initiatorForm = $event"
      />
      <div class="d-flex" style="gap: 1rem">
        <LmButton
          value="Cancelar"
          buttonClass="btn-secondary"
          @click="onInitiatorEditCancel"
        />
        <LmButton
          value="Confirmar"
          buttonClass="btn-primary"
          @click="onUpdateInitiator"
        />
      </div>
    </b-modal>

    <b-modal
      id="linked-processes-modal"
      title="Trámites asociados"
      hide-footer
      centered
    >
      <b-list-group>
        <b-list-group-item
          v-for="(item, index) in data.processRelationships"
          :key="index"
        >
          <span class="d-flex justify-content-between align-items-center">
            <span class="d-flex align-items-center">
              <b-link
                :to="processPath(item)"
                class="mr-2"
              >
                {{ item.name }}
              </b-link>
              <b-badge>{{ item.code }}</b-badge>
            </span>
            <i class="mdi mdi-delete clickable" v-b-popover.hover.top="'Eliminar'" @click="onRemoveProcessLink(item)"></i>
          </span>
        </b-list-group-item>
      </b-list-group>
    </b-modal>
  </b-row>
</template>

<script>
// Components
import LmButton from '@/components/form/elements/LmButton'
import LmFormBuilder from '@/components/LmFormBuilder'
// Utils
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts'

export default {
  mixins: [lmToasts],
  components: {
    LmButton,
    LmFormBuilder
  },
  data () {
    return {
      coverPageInPdf: {
        src: null,
        name: null
      },
      currentStarter: null,
      initiatorData: null,
      initiatorForm: {}
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    actions: {
      type: Object,
      require: true
    }
  },
  computed: {
    currentMovementInfo () {
      const destination = this.data.lastMovement.destination
      const type = Object.keys(destination).find(el => destination[el] !== null)
      const names = {
        area: this.data.lastMovement?.destination?.user?.area?.name || '',
        subarea: this.data.lastMovement?.destination?.user?.subarea?.name || '',
        group: this.data.lastMovement?.destination?.user?.group?.name || '',
        user: 'usuario'
      }
      let nameToDisplay = destination[type]?.name
      if (type === 'user' && !destination[type]?.name) {
        nameToDisplay = `${destination[type]?.firstname} ${destination[type]?.lastname}`
      }
      return {
        key: type,
        typeName: names[type],
        name: nameToDisplay,
        area: names?.area,
        subarea: names?.subarea
      }
    },
    starterType () {
      return this.data.processStarter.starterType
    },
    starterTypeId () {
      return this.data.processStarter.starterTypeId
    }
  },
  methods: {
    validateForm (validation) {
      validation.$touch()
      if (validation.$error) {
        // this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        return false
      } else {
        if (validation.$pending || validation.$error) {
          return false
        }
      }
      return true
    },
    async onViewCoverPage () {
      try {
        const doc = await commonsApi.axiosGetWrapper(`/processes/coverpage/${this.$route.params.id}`)

        this.coverPageInPdf.src = doc.data.src
        this.coverPageInPdf.name = doc.data.originalName
        this.$bvModal.show('coverPagePdfModal')
      } catch (error) {
        console.log(error)
      }
    },
    async getInitiatorData () {
      try {
        const res = await commonsApi.axiosGetWrapper(`/initiatortypes/${this.starterTypeId}`)
        this.initiatorData = {
          id: res.data._id,
          formbuilderPath: `/formbuilder/${res.data.formbuilderId}`,
          prefix: res.data.initiatorIdField
        }
      } catch (error) {
        throw error
      }
    },
    async onSearchStarterData () {
      try {
        let targetEndpoint
        if (this.starterType === 'people') {
          targetEndpoint = `/people/${this.data.processStarter.id}`
        } else {
          targetEndpoint = `/processes/${this.data._id}`
        }

        const res = await commonsApi.axiosGetWrapper(targetEndpoint)

        if (this.starterType === 'people') {
          this.currentStarter = res.data
        } else {
          this.initiatorForm = res.data.initiatorFields
          await this.getInitiatorData()
        }
      } catch (error) {
        console.log(error)
      }
    },
    async onStarterDetails () {
      try {
        if (this.starterType === 'people') {
          this.$bvModal.show('person-new-or-edit-modal')
        } else {
          await
          this.$bvModal.show('initiator-edit-modal')
        }
      } catch (error) {
        console.log(error)
      }
    },
    onUpdatePerson (event) {
      this.$emit('onUpdatePerson', {
        id: event._id,
        name: event.personName,
        starterTypeId: event.peopleTypeId,
        starterType: 'people'
      })
      this.$bvModal.hide('person-new-or-edit-modal')
    },
    async onUpdateInitiator () {
      const valid = await this.validateForm(this.$refs['lmFormbuilder-initiator'].$v)
      if (valid) {
        this.$emit('onUpdateInitiator', {
          ...this.initiatorForm[this.initiatorData.prefix.id],
          starterTypeId: this.initiatorData.id,
          fields: this.initiatorForm,
          starterType: 'initiator'
        })
        this.$bvModal.hide('initiator-edit-modal')
      }
    },
    onPeopleNewOrEditCancel () {
      this.$bvModal.hide('person-new-or-edit-modal')
    },
    onInitiatorEditCancel () {
      this.$bvModal.hide('initiator-edit-modal')
    },
    async onRemoveProcessLink (item) {
      try {
        await commonsApi.axiosPutWrapper('/processes/removerelationship', {
          id: this.$route.params.id,
          relatedProcessId: item.id
        })
        this.lmToastSuccessMessage(`Se eliminó ${item.name} correctamente.`, 'Acción realizada con éxito.')
        this.$emit('onRemoveProvessLink', item.id)
        if (!this.data.processRelationships.length) {
          this.$bvModal.hide('linked-processes-modal')
        }
      } catch (error) {
        console.log(error)
      }
    },
    processPath (event) {
      return {
        name: 'process-editar',
        params: {
          processTypeId: event.typeId,
          id: event.id,
          goBackButton: true
        }
      }
    }
  },
  async created () {
    try {
      await this.onSearchStarterData()
    } catch (error) {
      console.log(error)
    }
  }
}
</script>

<style lang="sass" scoped>
  .card
    height: 220px
  i.mdi
    font-size: 1rem
  .show-details-button
    &:focus
      box-shadow: none
</style>
